/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React from 'react';
import PropTypes from 'prop-types';

import Heading from '@react/react-spectrum/Heading';
import { MenuItem } from '@react/react-spectrum/Menu';
import DropdownButton from '@react/react-spectrum/DropdownButton';
import ChevronDown from '@react/react-spectrum/Icon/ChevronDown';
import { defineMessages, useIntl } from 'react-intl';

import { useProductAdminContext } from './ProductAdminContext';
import isProduct from '../../utils/isProduct';

import classes from './Breadcrumb.scss';

const messages = defineMessages({
    searchResults: {
        id: 'headline.searchResults',
        defaultMessage: 'Search Results'
    }
});

const Breadcrumb = props => {
    const [
        { config, searchTerm, categories, configurableProduct },
        { setConfig, setCategories, setConfigurableProduct }
    ] = useProductAdminContext();
    const { formatMessage } = useIntl();

    const SEARCH_RESULTS = formatMessage(messages.searchResults);

    if (!config) {
        return (
            <Heading variant="subtitle1" className={classes.heading}>
                {props.headline}
            </Heading>
        );
    }

    // Construct path from context
    let path = [props.headline, config, ...categories];
    if (searchTerm) {
        path.push(SEARCH_RESULTS);
    }
    if (configurableProduct) {
        path.push(configurableProduct);
    }

    const handleSelect = index => {
        if (index === 0) {
            // Return to root by setting config to null
            setConfig(null);
            return;
        }

        // Get item from index
        let item = path[index];
        if (!item) return;

        if (item.__typename === 'Configuration') {
            // Return to root category of config
            setConfig(item);
            return;
        }

        if (item.__typename === 'CategoryTree') {
            // Return to category
            // Construct new category path up until the selected category
            let newPath = [];
            for (let category of categories) {
                newPath.push(category);
                if (category.identifier === item.identifier) {
                    break;
                }
            }
            setCategories(newPath);
            return;
        }

        if (item === SEARCH_RESULTS) {
            setConfigurableProduct(null);
        }
    };

    // Convert path to labels
    const labels = path.map(item => {
        if (typeof item === 'string') return item;
        if (item.__typename === 'Configuration')
            return item.name !== 'Commerce' ? item.name : item.identifier.split('-').join(' ');
        if (item.__typename === 'CategoryTree' || isProduct(item.__typename)) return item.name;
    });

    // Remove last label since it is added as non select-able breadcrumb label
    const lastLabel = labels.pop();

    const menuItems = labels.map((item, index) => (
        <MenuItem role="menuitemradio" key={index} value={index}>
            {item}
        </MenuItem>
    ));

    return (
        <DropdownButton className={classes.root} label={lastLabel} icon={<ChevronDown />} onSelect={handleSelect}>
            {menuItems.reverse()}
        </DropdownButton>
    );
};

Breadcrumb.propTypes = {
    headline: PropTypes.string.isRequired
};

export default Breadcrumb;
